import React, { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";
import { type RouteObject } from "react-router";
import SidebarLayout from "./layouts/SidebarLayout";
import SuspenseLoader from "./components/SuspenseLoader";
import BaseLayout from "./layouts/BaseLayout";

const Loader = (Component: React.ComponentType): React.ComponentType => {
  const WrappedComponent: React.FC = (props: any) => (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

  WrappedComponent.displayName = `Loader(${Component.displayName ?? Component.name ?? "Component"})`;

  return WrappedComponent;
};

const Favorite = Loader(
  lazy(async () => await import("src/content/pages/Favorite")),
);
const FavoriteDetail = Loader(
  lazy(
    async () =>
      await import("src/content/pages/Favorite/components/FavoriteDetail"),
  ),
);
const FavoriteCreate = Loader(
  lazy(
    async () =>
      await import("src/content/pages/Favorite/components/FavoriteCreate"),
  ),
);
const User = Loader(lazy(async () => await import("src/content/pages/User")));
const UserDetails = Loader(
  lazy(
    async () => await import("src/content/pages/User/components/UserDetails"),
  ),
);
const NavigationHistoryDetail = Loader(
  lazy(
    async () =>
      await import(
        "src/content/pages/NavigationHistory/components/NavigationHistoryDetail"
      ),
  ),
);
const MyRoute = Loader(
  lazy(async () => await import("src/content/pages/MyRoute")),
);
const Status404 = Loader(
  lazy(async () => await import("src/content/pages/Status404")),
);
const RouteDetails = Loader(
  lazy(
    async () =>
      await import("src/content/pages/MyRoute/components/RouteDetails"),
  ),
);
const Friend = Loader(
  lazy(async () => await import("src/content/pages/Friend")),
);
const FriendDetail = Loader(
  lazy(
    async () =>
      await import("src/content/pages/Friend/components/FriendDetail"),
  ),
);
const FriendNavigationDetail = Loader(
  lazy(
    async () =>
      await import(
        "src/content/pages/Friend/components/FriendNavigationDetail"
      ),
  ),
);
const Notification = Loader(
  lazy(async () => await import("src/content/pages/Notification")),
);

const NavigationHistoryDownload = Loader(
  lazy(async () => await import("src/content/pages/NavigationHistoryDownload")),
);
const PushNotification = Loader(
  lazy(async () => await import("src/content/pages/PushNotification")),
);

const PushNotificationCreate = Loader(
  lazy(
    async () =>
      await import(
        "src/content/pages/PushNotification/components/PushNotificationCreate"
      ),
  ),
);

const PushNotificationDetail = Loader(
  lazy(
    async () =>
      await import(
        "src/content/pages/PushNotification/components/PushNotificationDetail"
      ),
  ),
);

const QRCode = Loader(
  lazy(async () => await import("src/content/pages/QRCode")),
);

const Consent = Loader(
  lazy(async () => await import("src/content/pages/Consent")),
);
const ConsentVersionForm = Loader(
  lazy(
    async () =>
      await import("src/content/pages/Consent/components/ConsentVersionForm"),
  ),
);

const routes: RouteObject[] = [
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="favorite" replace />,
      },
      {
        path: "favorite",
        element: <Favorite />,
      },
      {
        path: "favorite/create",
        element: <FavoriteCreate />,
      },
      {
        path: "favorite/:userId/:favoriteId",
        element: <FavoriteDetail />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="user" replace />,
      },
      {
        path: "user",
        element: <User />,
      },
      {
        path: "user/:userId",
        element: <UserDetails />,
      },
      {
        path: "navigation_history/:navigationId",
        element: <NavigationHistoryDetail />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="friend" replace />,
      },
      {
        path: "friend",
        element: <Friend />,
      },
      {
        path: "friend/:userId",
        element: <FriendDetail />,
      },
      {
        path: "friend/navigation/:navigationId",
        element: <FriendNavigationDetail />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="my-route" replace />,
      },
      {
        path: "my-route",
        element: <MyRoute />,
      },
      {
        path: "route/:routeId",
        element: <RouteDetails />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="notification" replace />,
      },
      {
        path: "notification",
        element: <Notification />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="push-notification" replace />,
      },
      {
        path: "push-notification",
        element: <PushNotification />,
      },
      {
        path: "push-notification/create",
        element: <PushNotificationCreate />,
      },
      {
        path: "push-notification/:pushId",
        element: <PushNotificationDetail />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        element: <Navigate to="navigation-history-download" replace />,
      },
      {
        path: "navigation-history-download",
        element: <NavigationHistoryDownload />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="qr-code" replace />,
      },
      {
        path: "qr-code",
        element: <QRCode />,
      },
    ],
  },
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="consent" replace />,
      },
      {
        path: "consent",
        element: <Consent />,
      },
      {
        path: "consent/new",
        element: <ConsentVersionForm />,
      },
      {
        path: "consent/:typeId/:regionId/:version",
        element: <ConsentVersionForm />,
      },
    ],
  },
  {
    path: "",
    element: <BaseLayout />,
    children: [
      {
        path: "*",
        element: <Status404 />,
      },
    ],
  },
];

export default routes;
